import React, { useEffect, useMemo, useRef, useState } from "react";
import { Camera, CameraOff, Mic, MicOff, Settings2, UserRound, VideoOff } from "lucide-react";
import {
  applyAudioOutputDevice,
  persistAudioOutputDeviceId,
  playTestSpeakerSound,
  readStoredAudioOutputDeviceId,
  resolveAudioOutputDeviceId,
  supportsAudioOutputSelection,
} from "../utils/liveInterviewAudioOutput";
/**
 * Enterprise-grade pre-join preview, modelled after Google Meet / Zoom.
 *
 *  - Dark video frame with rounded corners and subtle gradient.
 *  - Camera-off state shows a centred avatar with the user's initial when available.
 *  - Floating control bar at the bottom-centre with circular icon buttons
 *    (mic / camera / device settings).
 *  - Live audio activity meter — five bars beside the mic that animate
 *    with the user's voice so they can confirm the right mic is selected.
 *  - Device picker drawer (mic, speaker, camera) revealed via the settings cog.
 *
 *  All visual styling is scoped to .li-prejoin* in LiveInterview.css.
 */
const PreJoin = ({
  cameraOn = false,
  micOn = false,
  onCameraChange,
  onMicChange,
  speakerDeviceId = "",
  audioDeviceId = "",
  videoDeviceId = "",
  onSpeakerChange,
  onAudioDeviceChange,
  onVideoDeviceChange,
  displayName = "",
}) => {
  const videoRef = useRef(null);
  const speakerProbeRef = useRef(null);
  const streamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const audioRafRef = useRef(null);

  const [error, setError] = useState("");
  const [audioLevel, setAudioLevel] = useState(0);
  const [microphoneDetected, setMicrophoneDetected] = useState(false);
  const [audioDevices, setAudioDevices] = useState([]);
  const [speakerDevices, setSpeakerDevices] = useState([]);
  const [videoDevices, setVideoDevices] = useState([]);
  const [selectedAudioDevice, setSelectedAudioDevice] = useState(
    () => String(audioDeviceId || "")
  );
  const [selectedSpeakerDevice, setSelectedSpeakerDevice] = useState(
    () => String(speakerDeviceId || readStoredAudioOutputDeviceId() || "")
  );
  const [selectedVideoDevice, setSelectedVideoDevice] = useState(
    () => String(videoDeviceId || "")
  );
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [speakerSelectionSupported, setSpeakerSelectionSupported] = useState(
    () => supportsAudioOutputSelection()
  );
  const [speakerTestBusy, setSpeakerTestBusy] = useState(false);
  const [speakerTestMessage, setSpeakerTestMessage] = useState("");

  const initial = useMemo(() => {
    const safeName = String(displayName || "").trim();
    return safeName.charAt(0).toUpperCase();
  }, [displayName]);

  // ── stream lifecycle ──────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const start = async () => {
      await refreshDeviceList();
      if (!cameraOn && !micOn) return;
      try {
        await ensureStream({ enableAudio: micOn, enableVideo: cameraOn });
        if (cancelled) return;
        syncTracks(cameraOn, micOn);
        await refreshDeviceList();
      } catch (err) {
        console.error("[LiveInterview] Pre-join preview failed", err);
        if (cancelled) return;
        onCameraChange?.(false);
        onMicChange?.(false);
        setError(
          "Allow camera and microphone in your browser to preview before joining."
        );
      }
    };

    start();

    const onDeviceChange = () => {
      void refreshDeviceList();
    };
    navigator.mediaDevices?.addEventListener?.("devicechange", onDeviceChange);

    return () => {
      cancelled = true;
      stopAudioMeter();
      streamRef.current?.getTracks?.().forEach((track) => track.stop());
      navigator.mediaDevices?.removeEventListener?.("devicechange", onDeviceChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSpeakerSelectionSupported(supportsAudioOutputSelection());
  }, []);

  useEffect(() => {
    if (!cameraOn) return;
    const videoElement = videoRef.current;
    const stream = streamRef.current;
    if (!videoElement || !stream) return;
    if (videoElement.srcObject !== stream) {
      videoElement.srcObject = stream;
    }
    void videoElement.play?.().catch(() => {});
  }, [cameraOn]);

  useEffect(() => {
    syncTracks(cameraOn, micOn);
    if (micOn) {
      startAudioMeter();
    } else {
      stopAudioMeter();
      setAudioLevel(0);
      setMicrophoneDetected(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraOn, micOn]);

  useEffect(() => {
    const deviceId = String(selectedAudioDevice || "").trim();
    if (!deviceId) return;
    onAudioDeviceChange?.(deviceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAudioDevice]);

  useEffect(() => {
    const deviceId = String(selectedVideoDevice || "").trim();
    if (!deviceId) return;
    onVideoDeviceChange?.(deviceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVideoDevice]);

  useEffect(() => {
    if (!selectedSpeakerDevice) return undefined;
    persistAudioOutputDeviceId(selectedSpeakerDevice);
    onSpeakerChange?.(selectedSpeakerDevice);
    if (supportsAudioOutputSelection()) {
      void applyAudioOutputDevice(selectedSpeakerDevice, speakerProbeRef.current);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSpeakerDevice]);

  const syncTracks = (nextCameraOn, nextMicOn) => {
    streamRef.current?.getVideoTracks?.().forEach((track) => {
      track.enabled = nextCameraOn;
    });
    streamRef.current?.getAudioTracks?.().forEach((track) => {
      track.enabled = nextMicOn;
    });
  };

  const ensureStream = async ({
    audioDeviceId = "",
    videoDeviceId = "",
    enableAudio = micOn,
    enableVideo = cameraOn,
  } = {}) => {
    const needsAudioTrack = Boolean(enableAudio || audioDeviceId);
    const needsVideoTrack = Boolean(enableVideo || videoDeviceId);
    const hasAudioTrack = Boolean(streamRef.current?.getAudioTracks?.().length);
    const hasVideoTrack = Boolean(streamRef.current?.getVideoTracks?.().length);

    if (
      streamRef.current &&
      !audioDeviceId &&
      !videoDeviceId &&
      (!needsAudioTrack || hasAudioTrack) &&
      (!needsVideoTrack || hasVideoTrack)
    ) {
      if (videoRef.current && videoRef.current.srcObject !== streamRef.current) {
        videoRef.current.srcObject = streamRef.current;
      }
      return streamRef.current;
    }

    if (!needsAudioTrack && !needsVideoTrack) {
      return null;
    }

    // Stop existing tracks if we're switching device.
    streamRef.current?.getTracks?.().forEach((track) => track.stop());
    const constraints = {
      video: needsVideoTrack
        ? videoDeviceId
          ? { deviceId: { exact: videoDeviceId } }
          : true
        : false,
      audio: needsAudioTrack
        ? {
            ...(audioDeviceId ? { deviceId: { exact: audioDeviceId } } : {}),
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          }
        : false,
    };
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    streamRef.current = stream;
    if (videoRef.current) videoRef.current.srcObject = stream;
    return stream;
  };

  const refreshDeviceList = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audio = devices.filter((d) => d.kind === "audioinput");
      const speakers = devices.filter((d) => d.kind === "audiooutput");
      const video = devices.filter((d) => d.kind === "videoinput");
      setAudioDevices(audio);
      setSpeakerDevices(speakers);
      setVideoDevices(video);
      const currentAudio = streamRef.current?.getAudioTracks?.()[0];
      const currentVideo = streamRef.current?.getVideoTracks?.()[0];
      const currentAudioSettings = currentAudio?.getSettings?.();
      const currentVideoSettings = currentVideo?.getSettings?.();
      if (currentAudioSettings?.deviceId) setSelectedAudioDevice(currentAudioSettings.deviceId);
      if (currentVideoSettings?.deviceId) setSelectedVideoDevice(currentVideoSettings.deviceId);
      setSelectedSpeakerDevice((current) =>
        resolveAudioOutputDeviceId(
          speakers,
          current || speakerDeviceId || readStoredAudioOutputDeviceId()
        )
      );
    } catch (err) {
      console.warn("[LiveInterview] enumerateDevices failed", err);
    }
  };

  // ── audio meter ───────────────────────────────────────────────────────────
  const startAudioMeter = () => {
    if (!streamRef.current) return;
    const audioTrack = streamRef.current.getAudioTracks?.()[0];
    if (!audioTrack) return;

    try {
      stopAudioMeter();
      const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextCtor) return;
      const audioContext = new AudioContextCtor();
      audioContextRef.current = audioContext;
      void audioContext.resume?.().catch(() => {});
      const source = audioContext.createMediaStreamSource(streamRef.current);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.85;
      source.connect(analyser);
      analyserRef.current = analyser;

      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteFrequencyData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i += 1) sum += data[i];
        const avg = sum / data.length;
        // Map 0..255 → 0..1 with mild boost so small voices register.
        const level = Math.min(1, Math.max(0, (avg / 255) * 1.6));
        setAudioLevel(level);
        if (level >= 0.08) {
          setMicrophoneDetected(true);
        }
        audioRafRef.current = window.requestAnimationFrame(tick);
      };
      tick();
    } catch (err) {
      console.warn("[LiveInterview] Audio meter failed", err);
    }
  };

  const stopAudioMeter = () => {
    if (audioRafRef.current != null) {
      window.cancelAnimationFrame(audioRafRef.current);
      audioRafRef.current = null;
    }
    try {
      analyserRef.current?.disconnect?.();
    } catch {
      // ignore
    }
    analyserRef.current = null;
    try {
      audioContextRef.current?.close?.();
    } catch {
      // ignore
    }
    audioContextRef.current = null;
  };

  // ── handlers ──────────────────────────────────────────────────────────────
  const toggleCamera = async () => {
    setError("");
    try {
      const next = !cameraOn;
      await ensureStream({
        audioDeviceId: micOn ? selectedAudioDevice : "",
        videoDeviceId: next ? selectedVideoDevice : "",
        enableAudio: micOn,
        enableVideo: next,
      });
      onCameraChange?.(next);
      syncTracks(next, micOn);
    } catch {
      setError("Camera permission is needed for preview.");
    }
  };

  const toggleMic = async () => {
    setError("");
    try {
      const next = !micOn;
      setMicrophoneDetected(false);
      await ensureStream({
        audioDeviceId: next ? selectedAudioDevice : "",
        videoDeviceId: cameraOn ? selectedVideoDevice : "",
        enableAudio: next,
        enableVideo: cameraOn,
      });
      onMicChange?.(next);
      syncTracks(cameraOn, next);
    } catch {
      setError("Microphone permission is needed for preview.");
    }
  };

  const handleAudioDeviceChange = async (deviceId) => {
    try {
      setSelectedAudioDevice(deviceId);
      if (!micOn) return;
      setMicrophoneDetected(false);
      await ensureStream({
        audioDeviceId: deviceId,
        videoDeviceId: cameraOn ? selectedVideoDevice : "",
        enableAudio: micOn,
        enableVideo: cameraOn,
      });
      syncTracks(cameraOn, micOn);
      if (micOn) startAudioMeter();
    } catch (err) {
      console.warn("[LiveInterview] Switching microphone failed", err);
      setError("Could not switch microphone. Try a different device.");
    }
  };

  const handleVideoDeviceChange = async (deviceId) => {
    try {
      setSelectedVideoDevice(deviceId);
      if (!cameraOn) return;
      await ensureStream({
        audioDeviceId: micOn ? selectedAudioDevice : "",
        videoDeviceId: deviceId,
        enableAudio: micOn,
        enableVideo: cameraOn,
      });
      syncTracks(cameraOn, micOn);
      if (micOn) startAudioMeter();
    } catch (err) {
      console.warn("[LiveInterview] Switching camera failed", err);
      setError("Could not switch camera. Try a different device.");
    }
  };

  const applySpeakerDevice = async (deviceId) => {
    const nextId = String(deviceId || "").trim();
    setSelectedSpeakerDevice(nextId);
    persistAudioOutputDeviceId(nextId);
    onSpeakerChange?.(nextId);
    if (!nextId || !supportsAudioOutputSelection()) return;
    try {
      await applyAudioOutputDevice(nextId, speakerProbeRef.current);
    } catch (err) {
      console.warn("[LiveInterview] Switching speaker failed", err);
      setError("Could not switch speaker. Try a different device.");
    }
  };

  const handleSpeakerDeviceChange = async (deviceId) => {
    setSpeakerTestMessage("");
    await applySpeakerDevice(deviceId);
  };

  const handleTestSpeaker = async () => {
    setSpeakerTestMessage("");
    setSpeakerTestBusy(true);
    try {
      if (selectedSpeakerDevice && supportsAudioOutputSelection()) {
        await applyAudioOutputDevice(selectedSpeakerDevice, speakerProbeRef.current);
      }
      const played = await playTestSpeakerSound(selectedSpeakerDevice);
      setSpeakerTestMessage(played ? "Playing test sound" : "Could not play a test sound.");
    } catch (err) {
      console.warn("[LiveInterview] Speaker test failed", err);
      setSpeakerTestMessage("Could not play a test sound.");
    } finally {
      setSpeakerTestBusy(false);
    }
  };

  // ── render ────────────────────────────────────────────────────────────────
  const bars = [0.18, 0.36, 0.55, 0.74, 0.92];

  return (
    <div className="li-prejoin">
      <audio
        ref={speakerProbeRef}
        className="li-prejoin-speaker-probe"
        aria-hidden
        tabIndex={-1}
      />
      <div
        className={`li-prejoin-stage${settingsOpen ? " is-settings-open" : ""}`}
        data-camera={cameraOn ? "on" : "off"}
      >
        {cameraOn ? (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="li-prejoin-video"
          />
        ) : (
          <div className="li-prejoin-avatar" aria-hidden>
            <span className="li-prejoin-avatar-bubble">
              {initial || <UserRound size={40} strokeWidth={1.75} />}
            </span>
            <div className="li-prejoin-avatar-label">
              <VideoOff size={14} aria-hidden /> Camera off
            </div>
          </div>
        )}

        {/* Floating control bar */}
        <div className="li-prejoin-controls" role="group" aria-label="Pre-join controls">
          <button
            type="button"
            className={`li-prejoin-pill ${micOn ? "is-on" : "is-off"}`}
            onClick={toggleMic}
            aria-pressed={micOn}
            aria-label={micOn ? "Turn microphone off" : "Turn microphone on"}
            title={micOn ? "Turn microphone off" : "Turn microphone on"}
          >
            {micOn ? <Mic size={18} /> : <MicOff size={18} />}
          </button>

          {micOn ? (
            <>
              <span className="li-prejoin-divider" aria-hidden />
              <div
                className={`li-prejoin-mic-test ${microphoneDetected ? "is-detected" : ""}`}
                role="status"
                aria-live="polite"
                aria-atomic="true"
                aria-label={
                  microphoneDetected
                    ? "Microphone detected"
                    : "Speak to test microphone"
                }
              >
                <div className="li-prejoin-meter" aria-hidden>
                  {bars.map((threshold, i) => (
                    <span
                      key={i}
                      className="li-prejoin-meter-bar"
                      data-active={audioLevel >= threshold ? "true" : "false"}
                    />
                  ))}
                </div>
                <span className="li-prejoin-mic-test-copy" aria-hidden>
                  {microphoneDetected ? "Mic detected" : "Speak to test"}
                </span>
              </div>
            </>
          ) : null}

          <button
            type="button"
            className={`li-prejoin-pill ${cameraOn ? "is-on" : "is-off"}`}
            onClick={toggleCamera}
            aria-pressed={cameraOn}
            aria-label={cameraOn ? "Turn camera off" : "Turn camera on"}
            title={cameraOn ? "Turn camera off" : "Turn camera on"}
          >
            {cameraOn ? <Camera size={18} /> : <CameraOff size={18} />}
          </button>

          <span className="li-prejoin-divider" aria-hidden />

          <button
            type="button"
            className={`li-prejoin-pill ${settingsOpen ? "is-active" : ""}`}
            onClick={() => {
              setSettingsOpen((open) => {
                const next = !open;
                if (next) {
                  void (async () => {
                    try {
                      const needsLabels =
                        speakerDevices.length === 0 ||
                        speakerDevices.every((device) => !String(device.label || "").trim());
                      const hasLiveAudio = Boolean(
                        streamRef.current?.getAudioTracks?.().length
                      );
                      if (needsLabels && !hasLiveAudio) {
                        const probe = await navigator.mediaDevices.getUserMedia({
                          audio: true,
                          video: false,
                        });
                        probe.getTracks().forEach((track) => track.stop());
                      }
                    } catch {
                      // Permission may already be denied; still try to list devices.
                    }
                    await refreshDeviceList();
                  })();
                }
                return next;
              });
            }}
            aria-pressed={settingsOpen}
            aria-label="Microphone, speaker, and camera settings"
            title="Microphone, speaker, and camera settings"
          >
            <Settings2 size={18} />
          </button>
        </div>

        {/* Settings drawer for device picker */}
        {settingsOpen ? (
          <div className="li-prejoin-settings" role="dialog" aria-label="Audio and video settings">
            <div className="li-prejoin-settings-field">
              <label htmlFor="li-prejoin-mic">Microphone</label>
              <select
                id="li-prejoin-mic"
                value={selectedAudioDevice}
                onChange={(e) => handleAudioDeviceChange(e.target.value)}
              >
                {audioDevices.length === 0 ? (
                  <option value="">Default microphone</option>
                ) : (
                  audioDevices.map((d) => (
                    <option key={d.deviceId} value={d.deviceId}>
                      {d.label || `Microphone ${d.deviceId.slice(0, 6)}`}
                    </option>
                  ))
                )}
              </select>
            </div>
            <div className="li-prejoin-settings-field">
              <label htmlFor="li-prejoin-speaker">Speaker</label>
              <select
                id="li-prejoin-speaker"
                value={selectedSpeakerDevice}
                onChange={(e) => handleSpeakerDeviceChange(e.target.value)}
                disabled={!speakerSelectionSupported}
                title={
                  speakerDevices.find((device) => device.deviceId === selectedSpeakerDevice)
                    ?.label || "Speaker"
                }
              >
                {!speakerSelectionSupported ? (
                  <option value="">Speaker selection not supported in this browser</option>
                ) : speakerDevices.length === 0 ? (
                  <option value="">Default speaker</option>
                ) : (
                  speakerDevices.map((d) => (
                    <option key={d.deviceId} value={d.deviceId}>
                      {d.label || `Speaker ${d.deviceId.slice(0, 6)}`}
                    </option>
                  ))
                )}
              </select>
              <div className="li-prejoin-settings-field-row">
                <button
                  type="button"
                  className="li-prejoin-speaker-test"
                  onClick={handleTestSpeaker}
                  disabled={speakerTestBusy}
                >
                  {speakerTestBusy ? "Playing…" : "Test Speaker"}
                </button>
              </div>
              {speakerTestMessage ? (
                <p className="li-prejoin-speaker-test-status" role="status">
                  {speakerTestMessage}
                </p>
              ) : null}
            </div>
            <div className="li-prejoin-settings-field">
              <label htmlFor="li-prejoin-cam">Camera</label>
              <select
                id="li-prejoin-cam"
                value={selectedVideoDevice}
                onChange={(e) => handleVideoDeviceChange(e.target.value)}
              >
                {videoDevices.length === 0 ? (
                  <option value="">Default camera</option>
                ) : (
                  videoDevices.map((d) => (
                    <option key={d.deviceId} value={d.deviceId}>
                      {d.label || `Camera ${d.deviceId.slice(0, 6)}`}
                    </option>
                  ))
                )}
              </select>
            </div>
            <button
              type="button"
              className="li-prejoin-settings-close"
              onClick={() => setSettingsOpen(false)}
            >
              Done
            </button>
          </div>
        ) : null}
      </div>

      {error ? (
        <div className="li-prejoin-permission" role="alert">
          <strong>Camera and microphone access needed</strong>
          <p>{error}</p>
        </div>
      ) : null}
    </div>
  );
};

export default PreJoin;
